import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUserFormData } from "app/redux/selector";
import { activityConfig } from "app/containers/Features/containers/User/containers/Activity/components/activityMapping";
import { formatAuditLogDescription } from "app/containers/Features/containers/TeamSettings/container/AuditLogs/helper";
import { dateFormatter } from "utils/helpers";
import { selectTagList } from "app/redux/selector";
import { selectTeamMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { getMembersByIds } from "app/containers/Features/components/MemberSelection/helper";

export const useActivityItem = (item, selectedTeam) => {
  const user = useSelector(selectUserFormData);
  const members = useSelector(selectTeamMemberList);
  const tags = useSelector(selectTagList);

  const { label, description } = useMemo(() => {
    const getConfig = activityConfig[item.action];
    const label = getConfig?.label || item.action;

    let memberName = item.meta?.member_name || "You";
    const userId = item.user?._id || item.userId;

    if (userId && user?._id) {
      memberName =
        userId === user._id
          ? "You"
          : item.meta?.member_name || item.user?.firstName;
    }

    const mappedTemplateVariables = Object.entries(item.meta || {}).reduce(
      (acc: Record<string, string | string[]>, [key, value]) => {
        const formattedKey = key.replace(
          /[A-Z]/g,
          (letter) => `_${letter.toLowerCase()}`,
        );
        acc[formattedKey] = value as string | string[];
        return acc;
      },
      {} as Record<string, string | string[]>,
    );

    const enhancedMeta: Record<string, string | string[]> = {
      ...mappedTemplateVariables,
      ...(item.meta || {}),
      board_name: item.meta?.boardName || item.meta?.board_name,
      tag_name: item.meta?.tagName || item.meta?.tag_name,
      old_status: item.meta?.oldStatus || item.meta?.old_status,
      new_status: item.meta?.newStatus || item.meta?.new_status,
      status_name: item.meta?.statusName || item.meta?.status_name,
      project_name: item.meta?.projectName || item.meta?.project_name,
      workflow_name: item.meta?.workflowName || item.meta?.workflow_name,
      column_name: item.meta?.columnName || item.meta?.column_name,
    };

    if (enhancedMeta.assigneeName) {
      const assigneeIds = Array.isArray(enhancedMeta.assigneeName)
        ? enhancedMeta.assigneeName
        : [enhancedMeta.assigneeName as string];
      const memberNames = assigneeIds.map((id: string) => {
        let foundMember = getMembersByIds(members || [], id)[0];
        if (!foundMember) {
          foundMember = (members || []).find(
            (m) => m.userId === id,
          ) as (typeof members)[0];
        }
        return foundMember?.name ? foundMember.name : id;
      });
      enhancedMeta.assigneeName = memberNames.join(", ");
    }

    const mapTag = (tagVal: string | string[]) => {
      if (!tagVal) return tagVal;
      const tIds = Array.isArray(tagVal) ? tagVal : [tagVal];
      const tNames = tIds.map((id: string) => {
        const tag = (tags || []).find(
          (t: { _id: string; title?: string }) => t._id === id,
        );
        return tag ? tag.title || id : id;
      });
      return tNames.join(", ");
    };

    if (enhancedMeta.oldTagIds)
      enhancedMeta.oldTagIds = mapTag(
        enhancedMeta.oldTagIds as string | string[],
      );
    if (enhancedMeta.newTagIds)
      enhancedMeta.newTagIds = mapTag(
        enhancedMeta.newTagIds as string | string[],
      );
    if (enhancedMeta.tagName)
      enhancedMeta.tagName = mapTag(enhancedMeta.tagName as string | string[]);
    if (enhancedMeta.tag_name)
      enhancedMeta.tag_name = mapTag(
        enhancedMeta.tag_name as string | string[],
      );

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?)?$/;
    Object.keys(enhancedMeta).forEach((key) => {
      const val = enhancedMeta[key];
      if (typeof val === "string" && isoDateRegex.test(val)) {
        enhancedMeta[key] = dateFormatter(val, "datetime");
      }
    });

    let description = formatAuditLogDescription({
      activityName: item.action,
      activityDescription: enhancedMeta,
      displayMemberName: memberName,
      selectedTeam,
    });

    const grammarFixForYou = (text: string) => {
      return text
        .replace(/\bYou was\b/g, "You were")
        .replace(/\bYou has\b/g, "You have")
        .replace(/\bYou is\b/g, "You are");
    };

    description = grammarFixForYou(description);

    return { label, description };
  }, [item, selectedTeam, user, members, tags]);

  return { label, description };
};
